/**
 * The library sidebar: every decrypted video, plus playlists built from them.
 *
 * Clicking a video loads it into the active cell. Checking several and naming
 * them makes a playlist, which is just another encrypted item.
 */

import { useEffect, useMemo, useState } from 'react';

import {
  PRELOAD_BEST,
  isSlideshow,
  pickVariant,
  type Item,
  type LayoutId,
  type VideoPayload,
} from '../types';
import { useVault, type Coverage } from '../vault';

function bytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)}KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)}MB`;
  return `${(n / 1024 ** 3).toFixed(1)}GB`;
}

/**
 * Preload control: fetches every blob of a video so playback comes off the
 * disk cache instead of the network. On a slow connection this is the
 * difference between watching and waiting.
 */
function PreloadButton({ video }: { video: Item<VideoPayload> }) {
  const { preload, cancelPreload, preloads, cached, planFor, evict } = useVault();
  // The plain chip always takes the best rung; the chevron is how a different
  // quality gets chosen, per video, where the choice actually belongs.
  const target = PRELOAD_BEST;
  const variant = pickVariant(video.payload.variants, target);
  const quality = variant?.name ?? 'the best available';
  const state = preloads[video.uuid];
  const [plan, setPlan] = useState<string[] | null>(null);
  /** Rung picker for this video, overriding the library-wide default. */
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    let live = true;
    void planFor(video, target).then((p) => {
      if (live) setPlan(p);
    });
    return () => {
      live = false;
    };
  }, [planFor, video, target]);

  if ((video.payload.blobs?.length ?? 0) === 0) return null;

  // Percentage comes from the cache, not from this session's progress: a video
  // preloaded yesterday, or half-preloaded before a cancel, reports honestly on
  // a fresh page load.
  const total = plan?.length ?? 0;
  const have = plan ? plan.reduce((n, uuid) => n + (cached.has(uuid) ? 1 : 0), 0) : 0;
  const pct = total > 0 ? Math.round((have / total) * 100) : 0;
  const complete = total > 0 && have === total;

  if (state?.running) {
    return (
      <button
        type="button"
        className="chip chip--on"
        onClick={() => cancelPreload(video.uuid)}
        title={`Loading ${have}/${total} at ${quality} — ${bytes(state.bytes)}. Click to stop.`}
      >
        {pct}%
      </button>
    );
  }

  if (picking) {
    const rungs = [...(video.payload.variants ?? [])].sort((a, b) => a.height - b.height);
    return (
      <span className="row__picker">
        <span className="row__picker-label">load</span>
        {rungs.map((r) => (
          <button
            key={r.blob}
            type="button"
            className={cached.has(r.blob) ? 'chip chip--done' : 'chip'}
            onClick={() => {
              setPicking(false);
              preload(video, r.height);
            }}
            title={`Download ${r.name} (${r.width}x${r.height}) for this video`}
          >
            {r.name}
          </button>
        ))}
        <button type="button" className="chip" onClick={() => setPicking(false)} title="Cancel">
          ×
        </button>
      </span>
    );
  }

  return (
    <span className="row__load">
      <button
        type="button"
        className={complete ? 'chip chip--done' : 'chip'}
        onClick={() => (complete ? void evict(video) : preload(video, target))}
        title={
          complete
            ? `${quality} fully cached (${total} blobs). Click to remove from cache.`
            : have > 0
              ? `${have} of ${total} blobs cached at ${quality} — click to finish`
              : `Download this video at ${quality}`
        }
      >
        {complete ? '✓' : have > 0 ? `${pct}%` : '↓'}
      </button>
      <button
        type="button"
        className="chip"
        onClick={() => setPicking(true)}
        title="Choose a quality for this video"
        aria-label="Choose a quality for this video"
      >
        ⌄
      </button>
    </span>
  );
}

/**
 * Which rungs are on disk, and how completely.
 *
 * Derived from the cache rather than from anything we remember doing, so it
 * stays honest across reloads and after a partial or cancelled preload.
 */
function CachedRungs({ video }: { video: Item<VideoPayload> }) {
  const { coverageOf, cached } = useVault();
  const [coverage, setCoverage] = useState<Coverage | null>(null);

  useEffect(() => {
    let live = true;
    void coverageOf(video).then((c) => {
      if (live) setCoverage(c);
    });
    return () => {
      live = false;
    };
    // `cached` is the trigger: recompute whenever the cache changes.
  }, [coverageOf, video, cached]);

  if (!coverage || coverage.covered === 0) return null;

  // Coverage is over the timeline, not over any one rung: after watching, the
  // cache is a patchwork of whatever quality played at each moment, and what
  // matters is whether the whole video can be replayed offline.
  const complete = coverage.fraction >= 1;
  const percent = Math.round(coverage.fraction * 100);
  // Name the rung playback will actually open at. `rungs[0]` is the best
  // quality present anywhere, which reads as a promise the cache may not keep.
  const quality =
    coverage.startRung ?? (coverage.rungs.length ? `${coverage.rungs[0]}?` : 'partial');
  const mixed = coverage.rungs.length > 1;

  return (
    <span
      className="row__rungs"
      title={
        `${coverage.covered} of ${coverage.indices} segments cached\n` +
        coverage.perRung.map((r) => `${r.name}: ${r.have}/${r.total}`).join('\n') +
        `\nopens at: ${coverage.startRung ?? 'not cached'}`
      }
    >
      <span className={complete ? 'rung rung--full' : 'rung'}>
        {complete ? quality : `${percent}% · ${quality}`}
        {mixed ? ' +' : ''}
      </span>
    </span>
  );
}

/**
 * Two-step delete, used for playlists only.
 *
 * Videos are deleted from the CLI. Nothing in the browser destroys video data:
 * it is unrecoverable and expensive to re-upload, and a control that sits one
 * click away from the ones used constantly will eventually be hit.
 *
 * Playlists keep an in-app delete because they are cheap metadata with no CLI
 * equivalent — but still two steps, since they are just as unrecoverable.
 */
function DeleteButton({ label, onDelete }: { label: string; onDelete(): void }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  if (armed) {
    return (
      <span className="row__danger row__danger--armed">
        <button
          type="button"
          className="chip chip--danger"
          onClick={() => {
            setArmed(false);
            onDelete();
          }}
          title={`Permanently delete ${label}. This cannot be undone.`}
        >
          delete?
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => setArmed(false)}
          title="Keep it"
        >
          keep
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      className="chip row__danger"
      onClick={() => setArmed(true)}
      aria-label={`Delete ${label}`}
      title={`Delete ${label}`}
    >
      ×
    </button>
  );
}

function duration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function Thumb({ video }: { video: Item<VideoPayload> }) {
  const { thumbUrl } = useVault();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void thumbUrl(video).then((u) => {
      if (live) setUrl(u);
    });
    return () => {
      live = false;
    };
  }, [thumbUrl, video]);

  return url ? (
    <img className="row__thumb" src={url} alt="" loading="lazy" />
  ) : (
    <div className="row__thumb row__thumb--blank" />
  );
}

export interface SlotSummary {
  index: number;
  /** What is playing there, or null for an empty slot. */
  title: string | null;
}

interface Props {
  onPlay(video: Item<VideoPayload>): void;
  onPlayQueue(videoUuids: string[]): void;
  slots: SlotSummary[];
  /** The slot picks are sent to, or null when none is chosen. */
  target: number | null;
  onTarget(index: number): void;
  /** Enough to draw the wall in miniature. */
  layout: LayoutId;
  split: { x: number; y: number };
}

/**
 * Where the next pick lands, chosen here rather than on the grid.
 *
 * Aiming at a pane works with a cursor and a large screen. On a phone the
 * panes are small, and the library covers them anyway — so the destination was
 * being chosen in one place and the media in another, with the first hidden
 * behind the second.
 */
function SlotBar({
  slots,
  target,
  onTarget,
  layout,
  split,
}: Pick<Props, 'slots' | 'target' | 'onTarget' | 'layout' | 'split'>) {
  if (slots.length < 2) return null;

  // The same tracks the wall itself uses, at the same proportions, so a
  // dragged split shows here too. A list of numbered buttons made you work out
  // which number was the top-left pane; a picture of the wall does not.
  const columns = layout === 'cols2' || layout === 'grid4';
  const rows = layout === 'rows2' || layout === 'grid4';

  return (
    <div className="slotbar">
      <span className="slotbar__label">into</span>
      <div
        className="slotmap"
        role="group"
        aria-label="Destination slot"
        style={{
          gridTemplateColumns: columns ? `${split.x}fr ${1 - split.x}fr` : '1fr',
          gridTemplateRows: rows ? `${split.y}fr ${1 - split.y}fr` : '1fr',
        }}
      >
        {slots.map((slot) => (
          <button
            key={slot.index}
            type="button"
            className={slot.index === target ? 'slotmap__cell slotmap__cell--on' : 'slotmap__cell'}
            onClick={() => onTarget(slot.index)}
            aria-pressed={slot.index === target}
            title={
              slot.title ? `Slot ${slot.index + 1}: ${slot.title}` : `Slot ${slot.index + 1}, empty`
            }
          >
            {/* Number only. A title in a 30px box is unreadable, and this is
                a map of where things go — not a second copy of the library. */}
            <span className="slotmap__num">{slot.index + 1}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Where to move a video. Folders plus the way out of all of them. */
function MoveButton({ video }: { video: Item<VideoPayload> }) {
  const { playlists, moveVideo } = useVault();
  const [open, setOpen] = useState(false);
  const current = video.payload.folder ?? null;

  if (!open) {
    return (
      <button
        type="button"
        className="chip row__move"
        onClick={() => setOpen(true)}
        title="Move to a folder"
        aria-label="Move to a folder"
      >
        ⇢
      </button>
    );
  }

  return (
    <span className="row__picker">
      <span className="row__picker-label">move to</span>
      {playlists.map((f) => (
        <button
          key={f.uuid}
          type="button"
          className={current === f.uuid ? 'chip chip--on' : 'chip'}
          onClick={() => {
            setOpen(false);
            void moveVideo(video, f.uuid);
          }}
        >
          {f.payload.name}
        </button>
      ))}
      <button
        type="button"
        className={current === null ? 'chip chip--on' : 'chip'}
        onClick={() => {
          setOpen(false);
          void moveVideo(video, null);
        }}
      >
        unfiled
      </button>
      <button type="button" className="chip" onClick={() => setOpen(false)} title="Cancel">
        ×
      </button>
    </span>
  );
}

function VideoRow({
  video,
  onPlay,
  first,
  last,
}: {
  video: Item<VideoPayload>;
  onPlay(video: Item<VideoPayload>): void;
  first: boolean;
  last: boolean;
}) {
  const { reorderVideo } = useVault();

  return (
    <li className="row">
      <button type="button" className="row__main" onClick={() => onPlay(video)}>
        {/* Above the thumbnail rather than beside it: sharing the line with a
            thumbnail and three controls left so little room that every title
            ended in an ellipsis. */}
        <span className="row__title">{video.payload.title}</span>
        <Thumb video={video} />
        <span className="row__text">
          <span className="row__meta">
            {isSlideshow(video.payload) ? (
              // Not dimensions: a set has as many as it has photos, and the
              // one shown was whichever happened to sort first.
              <>
                {video.payload.slides?.length} slides · {duration(video.payload.duration)}
              </>
            ) : (
              <>
                {duration(video.payload.duration)} · {video.payload.width}×
                {video.payload.height}
                {video.payload.has_audio === false ? ' · silent' : ''}
              </>
            )}
          </span>
          <CachedRungs video={video} />
        </span>
      </button>
      <PreloadButton video={video} />
      <span className="row__order">
        <button
          type="button"
          className="chip row__move"
          title="Move up"
          aria-label={`Move ${video.payload.title} up`}
          disabled={first}
          onClick={() => void reorderVideo(video, -1)}
        >
          ↑
        </button>
        <button
          type="button"
          className="chip row__move"
          title="Move down"
          aria-label={`Move ${video.payload.title} down`}
          disabled={last}
          onClick={() => void reorderVideo(video, 1)}
        >
          ↓
        </button>
      </span>
      <MoveButton video={video} />
    </li>
  );
}

/** How long a folder takes to open or close. Matches `list--opening` in CSS. */
const COLLAPSE_MS = 150;

/**
 * Keep children mounted for the length of a closing animation.
 *
 * They cannot simply stay in the DOM to be animated out: every row in a
 * collapsed folder would fetch and decrypt a thumbnail, which is the cost
 * collapsing is meant to avoid. This holds them just long enough to play the
 * animation, then lets them go.
 */
function useClosing(open: boolean, ms: number): boolean {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = window.setTimeout(() => setMounted(false), ms);
    return () => window.clearTimeout(timer);
  }, [open, ms]);
  return mounted;
}

/** A folder and the videos pointing at it. */
function Folder({
  name,
  videos,
  onPlay,
  onPlayQueue,
  controls,
  defaultOpen,
}: {
  name: string;
  videos: Item<VideoPayload>[];
  onPlay(video: Item<VideoPayload>): void;
  onPlayQueue(uuids: string[]): void;
  controls?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const mounted = useClosing(open, COLLAPSE_MS);

  return (
    <section className="folder">
      <div className="folder__head">
        <button
          type="button"
          className="folder__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span className={open ? 'folder__caret folder__caret--open' : 'folder__caret'}>
            ▸
          </span>
          <span className="folder__name">{name}</span>
          <span className="count">{videos.length}</span>
        </button>
        {videos.length > 0 && (
          <button
            type="button"
            className="chip"
            onClick={() => onPlayQueue(videos.map((v) => v.uuid))}
            title={`Play all ${videos.length} in the selected slot`}
          >
            ▶
          </button>
        )}
        {controls}
      </div>
      {mounted && (
        <ul className={open ? 'list list--opening' : 'list list--closing'}>
          {videos.map((v, position) => (
            <VideoRow
              key={v.uuid}
              video={v}
              onPlay={onPlay}
              first={position === 0}
              last={position === videos.length - 1}
            />
          ))}
          {videos.length === 0 && <li className="library__empty">Empty — move a video here.</li>}
        </ul>
      )}
    </section>
  );
}

export function Library({
  onPlay,
  onPlayQueue,
  slots,
  target,
  onTarget,
  layout,
  split,
}: Props) {
  const { videos, playlists, broken, createFolder, renameFolder, deleteFolder, moveFolder, reload } =
    useVault();
  const [newFolder, setNewFolder] = useState('');
  const [busy, setBusy] = useState(false);

  // A folder id that no longer resolves means the folder was deleted; those
  // videos are unfiled rather than invisible.
  const known = useMemo(() => new Set(playlists.map((p) => p.uuid)), [playlists]);
  const grouped = useMemo(() => {
    const map = new Map<string, Item<VideoPayload>[]>();
    for (const f of playlists) map.set(f.uuid, []);
    const unfiled: Item<VideoPayload>[] = [];
    for (const v of videos) {
      const folder = v.payload.folder;
      if (folder && known.has(folder)) map.get(folder)!.push(v);
      else unfiled.push(v);
    }
    return { map, unfiled };
  }, [videos, playlists, known]);

  async function addFolder() {
    const name = newFolder.trim();
    if (!name) return;
    setBusy(true);
    try {
      await createFolder(name);
      setNewFolder('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="library">
      <div className="library__head">
        <button type="button" className="chip" onClick={() => void reload()} title="Reload">
          ⟳
        </button>
      </div>

      <SlotBar
        slots={slots}
        target={target}
        onTarget={onTarget}
        layout={layout}
        split={split}
      />

      {broken.length > 0 && (
        <p className="library__warn">
          {broken.length} item{broken.length === 1 ? '' : 's'} could not be decrypted.
        </p>
      )}

      {videos.length === 0 && (
        <p className="library__empty">
          Nothing here. Upload with the CLI: <code>peepshow upload video.mp4</code>
        </p>
      )}

      {playlists.map((f, position) => (
        <Folder
          key={f.uuid}
          name={f.payload.name}
          videos={grouped.map.get(f.uuid) ?? []}
          onPlay={onPlay}
          onPlayQueue={onPlayQueue}
          controls={
            <>
              <button
                type="button"
                className="chip row__danger"
                title="Move up"
                aria-label={`Move ${f.payload.name} up`}
                disabled={position === 0}
                onClick={() => void moveFolder(f.uuid, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="chip row__danger"
                title="Move down"
                aria-label={`Move ${f.payload.name} down`}
                disabled={position === playlists.length - 1}
                onClick={() => void moveFolder(f.uuid, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="chip row__danger"
                title="Rename"
                aria-label={`Rename ${f.payload.name}`}
                onClick={() => {
                  const name = prompt('Folder name', f.payload.name)?.trim();
                  if (name && name !== f.payload.name) void renameFolder(f.uuid, name);
                }}
              >
                ✎
              </button>
              <DeleteButton
                label={`the folder “${f.payload.name}” (its videos stay, unfiled)`}
                onDelete={() => void deleteFolder(f.uuid)}
              />
            </>
          }
        />
      ))}

      {(grouped.unfiled.length > 0 || playlists.length === 0) && (
        // Folders start collapsed; unfiled does not. With no folders yet it is
        // the whole library, and a collapsed one looks like an empty vault.
        <Folder
          name="Unfiled"
          videos={grouped.unfiled}
          onPlay={onPlay}
          onPlayQueue={onPlayQueue}
          defaultOpen
        />
      )}

      <div className="library__compose">
        <input
          placeholder="New folder"
          value={newFolder}
          onChange={(e) => setNewFolder(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void addFolder();
          }}
        />
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !newFolder.trim()}
          onClick={() => void addFolder()}
        >
          Add folder
        </button>
      </div>
    </aside>
  );
}
